if (typeof window.KrownRecentlyViewed === 'undefined') {
  const STORAGE_KEY = 'kt-recently-viewed';
  const LIMIT_KEY = 'kt-recently-viewed-limit';
  const DEFAULT_LIMIT = 8;

  const KrownRecentlyViewed = {
    getHandles() {
      try {
        const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((handle) => typeof handle === 'string' && handle !== '');
      } catch (error) {
        return [];
      }
    },

    setHandles(handles) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
      } catch (error) {
        // Private mode or storage blocked.
      }
    },

    getLimit() {
      const stored = parseInt(localStorage.getItem(LIMIT_KEY), 10);
      if (stored > 0) return stored;

      const themeLimit = Number(
        window.KROWN &&
          window.KROWN.settings &&
          window.KROWN.settings.recentlyViewed &&
          window.KROWN.settings.recentlyViewed.limit,
      );
      if (themeLimit > 0) return themeLimit;

      return DEFAULT_LIMIT;
    },

    setLimit(limit) {
      const next = parseInt(limit, 10);
      if (!(next > 0)) return;
      try {
        localStorage.setItem(LIMIT_KEY, String(next));
      } catch (error) {
        // Private mode or storage blocked.
      }
      this.setHandles(this.getHandles().slice(0, next));
    },

    saveHandle(handle, limit) {
      if (!handle) return;
      const cap = parseInt(limit, 10) || this.getLimit();
      const handles = this.getHandles().filter((item) => item !== handle);
      handles.unshift(handle);
      this.setHandles(handles.slice(0, cap));
    },

    isEnabled() {
      const setting = window.KROWN && window.KROWN.settings && window.KROWN.settings.recentlyViewed;
      return !setting || setting.enabled !== false;
    },
  };

  window.KrownRecentlyViewed = KrownRecentlyViewed;

  const currentHandle =
    window.KROWN &&
    window.KROWN.settings &&
    window.KROWN.settings.recentlyViewed &&
    window.KROWN.settings.recentlyViewed.handle;
  if (KrownRecentlyViewed.isEnabled() && currentHandle) {
    KrownRecentlyViewed.saveHandle(currentHandle);
  }
}

if (typeof customElements.get('recently-viewed-section') === 'undefined') {
  class RecentlyViewedSection extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[data-recently-viewed-scroller]');
      this.empty = this.querySelector('[data-recently-viewed-empty]');
      this.template = this.querySelector('[data-recently-viewed-template]');
      this.limit = parseInt(this.dataset.limit, 10) || window.KrownRecentlyViewed.getLimit();
      this.excludeHandle = this.dataset.excludeHandle || '';
      this.showTax = this.dataset.showTax === 'true';
      this.taxNote = this.dataset.taxNote || '';
      this.taxMultiplier = parseInt(this.dataset.taxMultiplier, 10) || 100;
      this.taxRoundUnit = parseInt(this.dataset.taxRoundUnit, 10) || 1;
      this.showFree = this.dataset.showFree === 'true';
      this.freeLabel = this.dataset.freeLabel || '';
      this.isDesignMode = !!(window.Shopify && window.Shopify.designMode);

      window.KrownRecentlyViewed.setLimit(this.limit);
      this.showEmpty();
      this.render();
    }

    async render() {
      if (!this.scroller) return;

      const handles = window.KrownRecentlyViewed.getHandles()
        .filter((handle) => handle !== this.excludeHandle)
        .slice(0, this.limit);

      if (!handles.length) {
        this.showEmpty();
        return;
      }

      const products = (
        await Promise.all(handles.map((handle) => this.fetchProduct(handle)))
      ).filter(Boolean);
      const validHandles = products.map((product) => product.handle);
      if (validHandles.length !== window.KrownRecentlyViewed.getHandles().length) {
        const kept = window.KrownRecentlyViewed.getHandles().filter(
          (handle) => validHandles.includes(handle) || handle === this.excludeHandle,
        );
        window.KrownRecentlyViewed.setHandles(kept);
      }

      if (!products.length) {
        this.showEmpty();
        return;
      }

      this.scroller.replaceChildren();
      products.forEach((product) => {
        const item = this.buildItem(product);
        if (item) this.scroller.appendChild(item);
      });

      this.classList.remove('kt-recently-viewed--force-visible');
      this.classList.add('kt-recently-viewed--has-products');
    }

    showEmpty() {
      // Storefront: hide the whole section when there are no items.
      // Theme editor: keep the section visible so merchants can still edit it.
      this.classList.remove('kt-recently-viewed--has-products');
      this.classList.toggle('kt-recently-viewed--force-visible', this.isDesignMode);
      if (this.scroller) this.scroller.replaceChildren();
    }

    fetchProduct(handle) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return fetch(`${root}products/${encodeURIComponent(handle)}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
    }

    buildItem(product) {
      if (!this.template) return null;

      const item = this.template.content.firstElementChild.cloneNode(true);
      const link = item.querySelector('[data-item-link]');
      const media = item.querySelector('[data-item-media]');
      const artist = item.querySelector('[data-item-artist]');
      const title = item.querySelector('[data-item-title]');
      const price = item.querySelector('[data-item-price]');

      if (link) link.setAttribute('href', product.url);
      if (artist) artist.textContent = product.vendor || '';
      if (title) title.textContent = product.title || '';
      if (price) price.innerHTML = this.formatPrice(product);
      if (media) {
        // The heart lives inside the media box, which is rebuilt per product,
        // so keep it aside and give it the product it now stands for.
        const wishlist = media.querySelector('[data-js-wishlist-button]');
        const badges = media.querySelector('[data-item-badges]');

        media.replaceChildren();
        media.appendChild(this.buildMedia(product));

        if (badges) {
          this.updateBadges(badges, product);
          media.appendChild(badges);
        }

        if (wishlist) {
          const variant = product.variants && product.variants[0];
          wishlist.dataset.productId = product.id || '';
          wishlist.dataset.productHandle = product.handle || '';
          wishlist.dataset.productUrl = product.url || '';
          wishlist.dataset.variantId = (variant && variant.id) || '';
          media.appendChild(wishlist);
        }
      }

      return item;
    }

    // Same rules as the 'product-badges-thumbnail' snippet, from /products/<handle>.js data.
    updateBadges(badges, product) {
      const sold = badges.querySelector('[data-item-badge="sold"]');
      const sale = badges.querySelector('[data-item-badge="sale"]');
      const price = Number(product.price);
      const compareAt = Number(product.compare_at_price);

      if (sold) sold.hidden = product.available !== false;
      if (!sale) return;

      sale.hidden = !(compareAt > price);
      if (sale.hidden) return;

      const type = sale.dataset.saleType;
      if (product.price_varies || product.compare_at_price_varies || !['percentage', 'amount'].includes(type)) {
        sale.textContent = sale.dataset.saleText || '';
      } else if (type === 'percentage') {
        sale.textContent = `-${Math.round(((compareAt - price) * 100) / compareAt)}%`;
      } else {
        const format =
          window.KROWN && window.KROWN.settings && window.KROWN.settings.shop_money_format;
        const saved = this.formatMoney(
          this.withTax(compareAt, product) - this.withTax(price, product),
          format
        );
        sale.innerHTML = (sale.dataset.saleAmountText || '').replace('[amount]', saved);
      }
    }

    // Prices are tax-exclusive; mirror the 'price-with-tax' snippet (round half up).
    withTax(cents, product) {
      const variant = product.variants && product.variants[0];
      const taxable = !(variant && variant.taxable === false);
      if (!taxable || this.taxMultiplier <= 100) return cents;

      const unit = this.taxRoundUnit;
      return Math.floor((cents * this.taxMultiplier + 50 * unit) / (100 * unit)) * unit;
    }

    buildMedia(product) {
      const imageUrl = product.featured_image;
      if (!imageUrl) {
        const placeholder = document.createElement('div');
        placeholder.className = 'kt-explore-works__placeholder';
        return placeholder;
      }

      const image = document.createElement('div');
      image.className = 'kt-image';

      const figure = document.createElement('figure');
      figure.className = 'kt-image__figure kt-image__figure--has-ratio';
      figure.style.paddingTop = '100%';

      const img = document.createElement('img');
      img.src = this.sizedImageUrl(imageUrl, 600);
      img.srcset = `${this.sizedImageUrl(imageUrl, 360)} 360w, ${this.sizedImageUrl(imageUrl, 600)} 600w, ${this.sizedImageUrl(imageUrl, 840)} 840w`;
      img.sizes = '(max-width: 767px) 70vw, 25vw';
      img.alt = product.title || '';
      img.width = 600;
      img.height = 600;
      img.loading = 'lazy';
      const markLoaded = () => image.classList.add('kt-loaded');
      if (img.complete) {
        markLoaded();
      } else {
        img.addEventListener('load', markLoaded, { once: true });
        img.addEventListener('error', markLoaded, { once: true });
      }

      figure.appendChild(img);
      image.appendChild(figure);
      return image;
    }

    sizedImageUrl(url, width) {
      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('width', String(width));
        return parsed.toString();
      } catch (error) {
        return url;
      }
    }

    formatPrice(product) {
      if (Number(product.price) === 0 && this.showFree) return this.escapeHtml(this.freeLabel);

      const cents = this.withTax(Number(product.price), product);

      const format =
        window.KROWN && window.KROWN.settings && window.KROWN.settings.shop_money_format;
      let html = this.formatMoney(cents, format);

      if (this.showTax && this.taxNote) {
        html += `<span class="kt-recently-viewed__tax">${this.escapeHtml(this.taxNote)}</span>`;
      }

      return html;
    }

    formatMoney(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const formatString = format || '{{amount}}';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = formatString.match(placeholderRegex);
      const iso = (window.KROWN && window.KROWN.settings && window.KROWN.settings.iso_code) || '';

      const formatWithDelimiters = (number, precision, thousands, decimal) => {
        thousands = thousands || ',';
        decimal = decimal || '.';
        if (isNaN(number) || number === null) return 0;
        number = (number / 100.0).toFixed(precision);
        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const centsAmount = parts[1] ? decimal + parts[1] : '';
        return dollarsAmount + centsAmount + iso;
      };

      let value = formatWithDelimiters(cents, 2);
      if (match) {
        switch (match[1]) {
          case 'amount':
            value = formatWithDelimiters(cents, 2);
            break;
          case 'amount_no_decimals':
            value = formatWithDelimiters(cents, 0);
            break;
          case 'amount_with_comma_separator':
            value = formatWithDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = formatWithDelimiters(cents, 0, '.', ',');
            break;
          case 'amount_no_decimals_with_space_separator':
            value = formatWithDelimiters(cents, 0, ' ');
            break;
          case 'amount_with_apostrophe_separator':
            value = formatWithDelimiters(cents, 2, "'");
            break;
        }
      }

      return formatString.replace(placeholderRegex, value);
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  customElements.define('recently-viewed-section', RecentlyViewedSection);
}
